
import Footer from './Footer'
import ProgressBar from './ProgressBar'
import CategoryStep from './steps/CategoryStep'
import SubcategoryStep from './steps/SubcategoryStep'
import OrderNumberStep from './steps/OrderNumberStep'
import OrderRedirectStep from './steps/OrderRedirectStep'
import TicketFormStep from './steps/TicketFormStep'
import TrainingInfoStep from './steps/TrainingInfoStep'
import ConfirmStep from './steps/ConfirmStep'
import { tr, trVars } from './translate'
import useSupportTicketWizard from './useSupportTicketWizard'
import global from '@/mixins/global'
import Headset from '@/assets/icons/support_ticket/headset.svg?inline'
import InfoCircle from '@/assets/icons/support_ticket/info-circle.svg?inline'
import ModalScreen from '@/components/common/ModalScreen'

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB, matches hs_file_upload's max:20971520
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg', 'application/pdf']

const initialForm = () => ({
  orderNum: '',
  message: '',
  hs_file_upload: null,
})

const initialErrors = () => ({
  orderNum: '',
  message: '',
  hs_file_upload: '',
  submit: '',
})

export default {
  components: {
    ModalScreen,
    Headset,
    InfoCircle,
    Footer,
    ProgressBar,
    CategoryStep,
    SubcategoryStep,
    OrderNumberStep,
    OrderRedirectStep,
    TicketFormStep,
    TrainingInfoStep,
    ConfirmStep,
  },
  mixins: [global, useSupportTicketWizard],
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    config: {
      type: Object,
      default: null,
    },
    configLoading: {
      type: Boolean,
      default: false,
    },
    configError: {
      type: [Boolean, Object],
      default: false,
    },
  },
  data() {
    return {
      submitting: false,
      checkingOrder: false,
      orderUuid: null,
      form: initialForm(),
      errors: initialErrors(),
    }
  },
  computed: {
    ui() {
      return this.config?.ui
    },
    canNextValue() {
      return this.wizardCanNext({
        orderNum: this.form.orderNum,
        message: this.form.message,
      })
    },
    currentStepTitle() {
      if (this.wizard.step === 'category') {
        return this.tr(this.ui?.stepTitles?.category)
      }
      if (this.wizard.step === 'subcategory') {
        return this.tr(this.wizard.category?.translations)?.label
      }
      if (this.wizard.step === 'order_num') {
        return this.tr(this.ui?.stepTitles?.order_num)
      }
      if (this.wizard.step === 'ticket_form') {
        return this.tr(this.ui?.stepTitles?.ticket_form)
      }
      return null
    },
  },
  methods: {
    tr(entry) {
      return tr(entry, this.$i18n.locale)
    },
    trVars(entry, vars) {
      return trVars(entry, this.$i18n.locale, vars)
    },
    validateFile(value) {
      if (!value) {
        return true
      }
      if (!ALLOWED_FILE_TYPES.includes(value.type)) {
        return this.$t('Unsupported file type')
      }
      if (value.size > MAX_FILE_SIZE) {
        return this.$t('Maximum file size is {max}MB', { max: 20 })
      }
      return true
    },
    onFileUpdate(file) {
      this.form.hs_file_upload = file
      const result = this.validateFile(file)
      this.errors.hs_file_upload = result === true ? '' : result
    },
    handleStartNew() {
      this.resetWizard()
      this.form = initialForm()
      this.errors = initialErrors()
      this.orderUuid = null
    },
    handleClose() {
      this.handleStartNew()
      this.$emit('close')
    },
    checkOrderAndAdvance() {
      this.errors.orderNum = ''
      this.checkingOrder = true
      this.$axios
        .$get('/v3/getReservation', {
          params: { reservation_id: this.form.orderNum, agent_id: this.authUser?.id },
        })
        .then((res) => {
          const uuid = res?.data?.uuid
          if (!uuid) {
            this.errors.orderNum = this.$t('Order not found')
            return
          }
          this.orderUuid = uuid
          this.advanceWizard()
        })
        .catch(() => {
          this.errors.orderNum = this.$t('Order not found')
        })
        .finally(() => {
          this.checkingOrder = false
        })
    },
    trySubmitTicket() {
      this.errors.message = this.form.message.trim()
        ? ''
        : this.$t('This field is required')
      const fileResult = this.validateFile(this.form.hs_file_upload)
      this.errors.hs_file_upload = fileResult === true ? '' : fileResult
      if (this.errors.message || this.errors.hs_file_upload) {
        return
      }
      this.submitTicket()
    },
    submitTicket() {
      // Backend reports/filters by main_subject/sub_subject, so send the
      // English label regardless of the agent's own UI locale.
      const mainSubject = tr(this.wizard.category?.translations, 'en')?.label
      const subSubject = this.wizard.subcategory
        ? tr(this.wizard.subcategory.translations, 'en')?.label
        : null
      const message = this.form.orderNum
        ? `${this.tr(this.ui?.labels?.order_number)}: ${this.form.orderNum}\n\n${this.form.message}`
        : this.form.message

      const fd = new FormData()
      fd.append('email', this.authUser?.email ?? '')
      fd.append('agent_id', this.authUser?.id ?? '')
      fd.append('type', this.wizard.category?.key ?? '')
      fd.append('main_subject', mainSubject ?? '')
      fd.append('message', message)
      fd.append(
        'subject',
        subSubject ? `${mainSubject} - ${subSubject}` : mainSubject,
      )
      if (subSubject) {
        fd.append('sub_subject', subSubject)
      }
      fd.append('hs_object_source_detail_2', window.location.href)
      if (this.form.hs_file_upload) {
        fd.append('hs_file_upload', this.form.hs_file_upload)
      }

      this.submitting = true
      this.$axios
        .$post('v3.1/admin/reservations/support-message', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((res) => {
          if (res.id) {
            this.completeWizardTicket(res.id)
          } else {
            this.errors.submit = this.$t('Something went wrong')
          }
        })
        .catch(() => {
          this.errors.submit = this.$t('Something went wrong')
        })
        .finally(() => {
          this.submitting = false
        })
    },
    handleFooterNext() {
      if (this.isWizardEndStep) {
        this.handleClose()
      } else if (this.wizard.step === 'ticket_form') {
        this.trySubmitTicket()
      } else if (this.wizard.step === 'order_num') {
        this.checkOrderAndAdvance()
      } else {
        this.advanceWizard()
      }
    },
  },
}
